import React, {
	SetStateAction,
	useState,
	useLayoutEffect,
	useContext
} from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './styles';
import Modal from 'components/Modal';
import SimpleInput from 'components/SimpleInput';
import { DataFormUserProps } from 'types/DataFormUserProps';
import { UserMondelezProps } from 'types/UserProps';
import { UseTranslationProps } from 'types/UseTranslationProps';
import { saveUser, editUser, editMyUser } from './reqs';
import SystemNotifyContext from 'context/SystemNotifyContext';
import LoadGeneralContext from 'context/LoadGeneralContext';
import RolesUserContext from 'context/RolesUserContext';
import { saveList, verifyForm } from './functions';
import { getUsers } from 'pages/Users/reqs';
import {
	canAcessAdminPanel,
	getObjOnLocalStorage,
	isValidEmail,
	removeSpecialCharacters
} from 'utils/functions';
import SelectInput from 'components/SelectInput';
import { SelectInputItemProps } from 'types/SelectInputItemProps';
import { FiltersUsersProps } from 'types/FiltersUsersProps';

interface FormUserProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<SetStateAction<boolean>>;
	userToEdit: null | UserMondelezProps;
	setUserToEdit: React.Dispatch<SetStateAction<null | UserMondelezProps>>;
	setListUsers: React.Dispatch<React.SetStateAction<UserMondelezProps[]>>;
	setQtdPages: React.Dispatch<React.SetStateAction<number>>;
	page: number;
	filters: FiltersUsersProps;
	justUsersAllowed: boolean;
}

const initialDataForm: DataFormUserProps = {
	firstName: '',
	lastName: '',
	phone: '',
	email: '',
	typeUser: '',
	password: '',
	passwordConfirm: ''
};

const initialDataFormError = {
	firstName: false,
	lastName: false,
	phone: false,
	email: false,
	roles: false,
	password: false,
	passwordConfirm: false
};

export default function FormUser({
	isOpen,
	setIsOpen,
	setUserToEdit,
	userToEdit,
	setListUsers,
	page,
	setQtdPages,
	filters,
	justUsersAllowed
}: FormUserProps) {
	const userData: UserMondelezProps = getObjOnLocalStorage();
	const canAcess = canAcessAdminPanel(userData?.authentication?.roles || []);

	const { t }: UseTranslationProps = useTranslation();
	const { handleNotify } = useContext(SystemNotifyContext);
	const { setIsOpenLoad, setMessage } = useContext(LoadGeneralContext);
	const { listRoles, isSearchingRoles } = useContext(RolesUserContext);

	const [dataForm, setDataForm] = useState(initialDataForm);
	const [dataFormErros, setDataFormErros] = useState(initialDataFormError);

	const noChangePassword = 'naoMudarSenha';

	function resetStatesComp() {
		setDataForm(initialDataForm);
		setUserToEdit(null);
	}

	async function handleSaveUser() {
		const canSave = verifyForm(dataForm, handleNotify, userToEdit);

		if (canSave) {
			setIsOpenLoad(true);
			setMessage('Salvando usuário');

			const resp = await saveUser(dataForm, handleNotify);

			setIsOpenLoad(false);

			if (resp.isSuccess && resp.newUser !== null) {
				await getUsers(
					{
						filterByCpf: removeSpecialCharacters(filters.filterByCpf),
						filterByEmail: filters.filterByEmail,
						justUsersAllowed: justUsersAllowed
					},
					page,
					setListUsers,
					setQtdPages,
					handleNotify
				);
				setIsOpen(false);
				resetStatesComp();
			}
		}
	}

	function returnListRoles() {
		let list: SelectInputItemProps[] = [];

		if (listRoles.length > 0) {
			listRoles.forEach((role) => {
				list.push({
					id: role.id,
					option: role.name
				});
			});
		}

		//quando o usuário logado não for sysadmin, não deixa ele criar outro sysadmin
		if (!canAcess) {
			list = list.filter(
				(role) => role.option.toLocaleLowerCase() !== 'sysadmin'
			);
		}

		return list;
	}

	async function handleEditUser() {
		const canSave = verifyForm(dataForm, handleNotify, userToEdit);

		if (canSave && userToEdit !== null) {
			setIsOpenLoad(true);
			setMessage('Editando usuário');

			const resp =
				userData.id === userToEdit.id
					? await editMyUser(
							userToEdit,
							dataForm,
							handleNotify,
							noChangePassword,
							listRoles
						)
					: await editUser(
							userToEdit,
							dataForm,
							handleNotify,
							noChangePassword,
							listRoles
						);

			setIsOpenLoad(false);

			if (resp.isSuccess && resp.newUser !== null && userToEdit !== null) {
				setIsOpenLoad(true);
				setMessage('Atualizando lista de usuários');
				await getUsers(
					{
						filterByCpf: removeSpecialCharacters(filters.filterByCpf),
						filterByEmail: filters.filterByEmail,
						justUsersAllowed: justUsersAllowed
					},
					page,
					setListUsers,
					setQtdPages,
					handleNotify
				);

				setIsOpenLoad(false);
				setIsOpen(false);
				resetStatesComp();
			}
		}
	}

	function findeOptionOnListByOption(
		value: string,
		list: SelectInputItemProps[]
	) {
		const itemsList = list.filter(
			(item) => item.option.toLowerCase() === value.toLowerCase()
		);
		if (itemsList.length > 0) {
			return itemsList[0];
		}

		return '';
	}

	//caso as permissões do usuário ainda não tenham carregado abre tela de load
	useLayoutEffect(() => {
		if (isSearchingRoles) {
			setIsOpenLoad(true);
			setMessage('Carregando permissões do usuário');
		} else {
			setIsOpenLoad(false);
		}
	}, [isSearchingRoles]);

	useLayoutEffect(() => {
		if (userToEdit !== null) {
			setDataForm({
				...initialDataForm,
				email: userToEdit?.email || '',
				firstName: userToEdit?.firstName || '',
				lastName: userToEdit?.lastName || '',
				password: noChangePassword,
				passwordConfirm: noChangePassword,
				phone: userToEdit?.phone || '',
				typeUser: findeOptionOnListByOption(
					userToEdit.authentication?.roles[0] || '',
					returnListRoles()
				)
			});
		}
	}, []);

	return (
		<Modal
			isOpenProps={isOpen}
			setIsOpenModal={() => {
				setIsOpen((value) => !value);
				resetStatesComp();
			}}
			title={t(
				userToEdit === null
					? 'users.formUser.addUser'
					: 'users.formUser.editUser'
			)}
			buttonClose={false}
		>
			<S.container>
				<div>
					<SimpleInput
						initialValue={dataForm.firstName}
						label={t('Primeiro nome')}
						error={dataFormErros.firstName}
						messagemError="*Preencha o nome"
						getValue={(value: string) => {
							if (value === '') {
								setDataFormErros((obj) => ({
									...obj,
									firstName: true
								}));
							} else {
								setDataFormErros((obj) => ({
									...obj,
									firstName: false
								}));
							}
							setDataForm((obj) => ({ ...obj, firstName: value }));
						}}
					/>
					<SimpleInput
						initialValue={dataForm.lastName}
						label={t('Sobrenome')}
						error={dataFormErros.lastName}
						messagemError="*Preencha o sobrenome"
						getValue={(value: string) => {
							if (value === '') {
								setDataFormErros((obj) => ({
									...obj,
									lastName: true
								}));
							} else {
								setDataFormErros((obj) => ({
									...obj,
									lastName: false
								}));
							}
							setDataForm((obj) => ({ ...obj, lastName: value }));
						}}
					/>
				</div>

				<SelectInput
					list={returnListRoles()}
					getValue={(value: SelectInputItemProps | '') => {
						setDataForm((obj) => ({
							...obj,
							typeUser: value === '' ? '' : value
						}));
					}}
					initialValue={
						dataForm.typeUser === ''
							? ''
							: findeOptionOnListByOption(
									dataForm.typeUser.option,
									returnListRoles()
								)
					}
					label="Tipo de usuário"
				/>

				<div>
					<SimpleInput
						label="E-mail"
						initialValue={dataForm.email}
						error={dataFormErros.email}
						messagemError="*Insira um e-mail válido"
						getValue={(value: string) => {
							if (!isValidEmail(value)) {
								setDataFormErros((obj) => ({
									...obj,
									email: true
								}));
							} else {
								setDataFormErros((obj) => ({
									...obj,
									email: false
								}));
							}
							setDataForm((obj) => ({ ...obj, email: value }));
						}}
						typeInput="email"
					/>
					<SimpleInput
						messagemError="*Insira um CPF válido"
						error={dataFormErros.phone}
						initialValue={dataForm.phone}
						label={t('Telefone')}
						getValue={(value: string) => {
							setDataFormErros((obj) => ({
								...obj,
								phone: false
							}));

							setDataForm((obj) => ({
								...obj,
								phone: value
							}));
						}}
					/>
				</div>

				<div>
					<SimpleInput
						initialValue={dataForm.password}
						label={t('users.formUser.password')}
						error={dataFormErros.password}
						getValue={(value: string) => {
							setDataForm((obj) => ({ ...obj, password: value }));
						}}
						typeInput="password"
					/>

					<SimpleInput
						initialValue={dataForm.passwordConfirm}
						label={t('users.formUser.repeatPassword')}
						getValue={(value: string) => {
							setDataForm((obj) => ({ ...obj, passwordConfirm: value }));
						}}
						typeInput="password"
					/>
				</div>

				<button style={{ display: 'none' }} onClick={saveList}>
					add list emails
				</button>
				<S.containerBtns>
					<S.cancel
						data-testid="cancelBtn"
						onClick={() => {
							setIsOpen(false);
							resetStatesComp();
						}}
					>
						{t('cancel')}
					</S.cancel>
					<S.save
						data-testid="saveBtn"
						onClick={() => {
							if (userToEdit === null) {
								handleSaveUser();
							} else {
								handleEditUser();
							}
						}}
					>
						{t('save')}
					</S.save>
				</S.containerBtns>
			</S.container>
		</Modal>
	);
}
